import React from 'react';
import { Box, Typography, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';

const ChartContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '16px',
  padding: '16px'
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '180px',
  height: '180px'
}));

const CenterText = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  textAlign: 'center'
}));

const LegendContainer = styled(Stack)(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: '12px',
  marginTop: '8px'
}));

const LegendItem = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '6px'
}));

const LegendDot = styled(Box)(({ color }) => ({
  width: '10px',
  height: '10px',
  borderRadius: '50%',
  backgroundColor: color
}));

const LegendText = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 500,
  color: '#64748b',
  fontFamily: 'Inter, sans-serif'
}));

// Star rating colors
const STAR_COLORS = {
  5: '#22c55e', // Green
  4: '#84cc16', // Lime
  3: '#eab308', // Yellow
  2: '#f97316', // Orange
  1: '#ef4444'  // Red
};

// Helper function to generate chart data from reputation data
const generateChartData = (reputationData) => {
  if (!reputationData?.reviews) {
    return {
      segments: [
        { label: '5 Stars', value: 60, color: STAR_COLORS[5], stars: 5 },
        { label: '4 Stars', value: 25, color: STAR_COLORS[4], stars: 4 },
        { label: '3 Stars', value: 10, color: STAR_COLORS[3], stars: 3 },
        { label: '2 Stars', value: 3, color: STAR_COLORS[2], stars: 2 },
        { label: '1 Star', value: 2, color: STAR_COLORS[1], stars: 1 }
      ],
      avgRating: reputationData?.yourBusiness?.rating || 4.5,
      totalReviews: reputationData?.yourBusiness?.reviewCount || 0
    };
  }

  const reviews = Array.isArray(reputationData.reviews) 
    ? reputationData.reviews 
    : (reputationData.reviews?.reviews || []);

  // Count reviews by star rating
  const starCounts = { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 };
  
  reviews.forEach(review => {
    let rating;
    if (typeof review.starRating === 'number') {
      rating = review.starRating;
    } else {
      const map = { ONE: 1, TWO: 2, THREE: 3, FOUR: 4, FIVE: 5 };
      rating = map[String(review.starRating || '').toUpperCase()] || 0;
    }
    if (rating >= 1 && rating <= 5) {
      starCounts[rating]++;
    }
  });

  const total = Object.values(starCounts).reduce((sum, count) => sum + count, 0);
  
  const segments = [5, 4, 3, 2, 1].map(stars => ({
    label: stars === 1 ? '1 Star' : `${stars} Stars`,
    value: total > 0 ? (starCounts[stars] / total) * 100 : 0,
    count: starCounts[stars],
    color: STAR_COLORS[stars],
    stars
  })).filter(segment => segment.value > 0);

  // Calculate average
  let avgRating = reputationData?.yourBusiness?.rating || 0;
  if (!avgRating && total > 0) {
    avgRating = Object.entries(starCounts).reduce((sum, [stars, count]) => 
      sum + (Number(stars) * count), 0) / total;
  }

  return {
    segments: segments.length > 0 ? segments : [
      { label: '5 Stars', value: 100, color: STAR_COLORS[5], stars: 5, count: 0 }
    ],
    avgRating: avgRating || 4.5,
    totalReviews: total || reputationData?.yourBusiness?.reviewCount || 0
  };
};

const DynamicPieChart = ({ data, colors, title, reputationData }) => {
  const chartInfo = generateChartData(reputationData);
  const { segments, avgRating, totalReviews } = chartInfo;
  
  const centerX = 90;
  const centerY = 90;
  const outerRadius = 85;
  const innerRadius = 55; // Donut hole

  function polarToCartesian(cx, cy, r, angleInDegrees) {
    const angleInRadians = (angleInDegrees - 90) * Math.PI / 180.0;
    return {
      x: cx + (r * Math.cos(angleInRadians)),
      y: cy + (r * Math.sin(angleInRadians))
    };
  }

  function createDonutPath(cx, cy, outerR, innerR, startAngle, endAngle) {
    if (endAngle - startAngle >= 359.99) {
      // Full circle donut
      return `
        M ${cx - outerR},${cy}
        A ${outerR},${outerR} 0 1,1 ${cx + outerR},${cy}
        A ${outerR},${outerR} 0 1,1 ${cx - outerR},${cy}
        M ${cx - innerR},${cy}
        A ${innerR},${innerR} 0 1,0 ${cx + innerR},${cy}
        A ${innerR},${innerR} 0 1,0 ${cx - innerR},${cy}
        Z
      `;
    }
    
    const outerStart = polarToCartesian(cx, cy, outerR, endAngle);
    const outerEnd = polarToCartesian(cx, cy, outerR, startAngle);
    const innerStart = polarToCartesian(cx, cy, innerR, startAngle);
    const innerEnd = polarToCartesian(cx, cy, innerR, endAngle);
    const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

    return `
      M ${outerStart.x} ${outerStart.y}
      A ${outerR} ${outerR} 0 ${largeArcFlag} 0 ${outerEnd.x} ${outerEnd.y}
      L ${innerStart.x} ${innerStart.y}
      A ${innerR} ${innerR} 0 ${largeArcFlag} 1 ${innerEnd.x} ${innerEnd.y}
      Z
    `;
  }

  // Calculate paths for each segment
  let currentAngle = 0;
  const paths = segments.map((segment, index) => {
    const angle = (segment.value / 100) * 360;
    const path = createDonutPath(
      centerX, 
      centerY, 
      outerRadius, 
      innerRadius, 
      currentAngle, 
      currentAngle + angle
    );
    currentAngle += angle;
    return { ...segment, path };
  });

  return (
    <ChartContainer>
      <ChartWrapper>
        <svg viewBox="0 0 180 180" width="180" height="180" xmlns="http://www.w3.org/2000/svg">
          {/* Background circle */}
          <circle 
            cx={centerX} 
            cy={centerY} 
            r={outerRadius} 
            fill="#f1f5f9"
          />
          
          {/* Donut segments */}
          {paths.map((segment, index) => (
            <path 
              key={index}
              d={segment.path}
              fill={segment.color}
              stroke="white"
              strokeWidth="2"
              style={{ 
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
            />
          ))}
          
          {/* Inner circle (donut hole) */}
          <circle 
            cx={centerX} 
            cy={centerY} 
            r={innerRadius - 2}
            fill="white"
          />
        </svg>
        
        {/* Center content */}
        <CenterText>
          <Typography 
            sx={{ 
              fontSize: '28px', 
              fontWeight: 700, 
              color: '#1e293b',
              fontFamily: 'Inter, sans-serif',
              lineHeight: 1
            }}
          >
            {avgRating.toFixed(1)}
          </Typography>
          <Typography 
            sx={{ 
              fontSize: '11px', 
              fontWeight: 500, 
              color: '#94a3b8',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}
          >
            Avg Rating
          </Typography>
        </CenterText>
      </ChartWrapper>
      
      {/* Legend */}
      <LegendContainer>
        {segments.slice(0, 5).map((segment, index) => (
          <LegendItem key={index}>
            <LegendDot color={segment.color} />
            <LegendText>
              {segment.label} {segment.count !== undefined ? `(${segment.count})` : `(${Math.round(segment.value)}%)`}
            </LegendText>
          </LegendItem>
        ))}
      </LegendContainer>
      
      {/* Total reviews */}
      <Typography 
        sx={{ 
          fontSize: '13px', 
          fontWeight: 600, 
          color: '#64748b',
          fontFamily: 'Inter, sans-serif',
          mt: 1
        }}
      >
        Based on {totalReviews} reviews
      </Typography>
    </ChartContainer>
  );
};

export default DynamicPieChart;
